import { FiHelpCircle } from 'react-icons/fi';

export const questionTagsData = {
  courseId: 'question-tags',
  courseType: 'grammar',
  title: 'Question Tags',
  icon: FiHelpCircle,
  overview: {
    impact: 'Sound more natural and fluent in Speaking',
    focus: 'Forming and using question tags correctly',
    uses: 'Speaking Parts 1-3',
    duration: '20 minutes'
  },
  sections: [
    {
      title: 'Basic Rules',
      content: `<p>A question tag confirms or checks information. The rule is simple:</p>`,
      points: [
        'Positive statement → negative tag: \'You like coffee, don\'t you?\'',
        'Negative statement → positive tag: \'She isn\'t coming, is she?\'',
        'Match the auxiliary/modal: \'He can swim, can\'t he?\'',
        'No auxiliary? Use do/does/did: \'You live here, don\'t you?\''
      ]
    },
    {
      title: 'Special Cases',
      content: `<p>Some question tags are irregular:</p>`,
      table: {
        headers: [
          'Statement',
          'Tag',
          'Note'
        ],
        rows: [
          [
            'I am late,',
            'aren\'t I?',
            'NOT \'amn\'t I\''
          ],
          [
            'Let\'s go,',
            'shall we?',
            'Suggestions'
          ],
          [
            'Don\'t touch that,',
            'will you?',
            'Commands'
          ],
          [
            'There\'s a problem,',
            'isn\'t there?',
            '\'There\' as subject'
          ],
          [
            'Nobody came,',
            'did they?',
            'Nobody/nothing = negative'
          ]
        ]
      }
    },
    {
      title: 'Intonation Matters',
      content: `<p>The way you say the tag changes the meaning:</p>`,
      points: [
        'Rising intonation ↗ = genuine question (you don\'t know): \'She\'s French, isn\'t she? ↗\'',
        'Falling intonation ↘ = expecting agreement: \'Nice day, isn\'t it? ↘\''
      ]
    }
  ],
  exercises: [
    {
      title: 'Add the Correct Tag',
      type: 'select',
      instructions: 'Choose the right question tag.',
      questions: [
        {
          text: 'She speaks English, ___?',
          options: [
            'doesn\'t she',
            'does she',
            'isn\'t she'
          ],
          answer: 'doesn\'t she',
          explanation: 'Positive + no auxiliary → negative tag with \'does\': doesn\'t she.'
        },
        {
          text: 'They haven\'t arrived, ___?',
          options: [
            'have they',
            'haven\'t they',
            'did they'
          ],
          answer: 'have they',
          explanation: 'Negative statement → positive tag: have they.'
        },
        {
          text: 'You can drive, ___?',
          options: [
            'can\'t you',
            'can you',
            'don\'t you'
          ],
          answer: 'can\'t you',
          explanation: 'Positive with \'can\' → negative: can\'t you.'
        },
        {
          text: 'I\'m right, ___?',
          options: [
            'aren\'t I',
            'amn\'t I',
            'am I'
          ],
          answer: 'aren\'t I',
          explanation: 'Special case: \'I am\' → \'aren\'t I\'.'
        }
      ]
    },
    {
      title: 'Choose the Correct Tag',
      type: 'select',
      instructions: 'Select the correct question tag for each sentence.',
      questions: [
        {
          text: 'You\'re coming to the party, ___?',
          options: [
            'aren\'t you',
            'are you',
            'don\'t you'
          ],
          answer: 'aren\'t you',
          explanation: 'Positive statement with \'are\' → negative tag: aren\'t you.'
        },
        {
          text: 'He won\'t be late, ___?',
          options: [
            'will he',
            'won\'t he',
            'is he'
          ],
          answer: 'will he',
          explanation: 'Negative statement with \'won\'t\' → positive tag: will he.'
        },
        {
          text: 'They were happy, ___?',
          options: [
            'weren\'t they',
            'were they',
            'didn\'t they'
          ],
          answer: 'weren\'t they',
          explanation: 'Positive statement with \'were\' → negative tag: weren\'t they.'
        },
        {
          text: 'Let\'s take a break, ___?',
          options: [
            'shall we',
            'will we',
            'don\'t we'
          ],
          answer: 'shall we',
          explanation: 'Special case: \'Let\'s\' always takes the tag \'shall we\'.'
        }
      ]
    },
    {
      title: 'Complete Question Tags',
      type: 'fill-in',
      instructions: 'Write the correct question tag to complete each sentence.',
      questions: [
        {
          text: 'She can swim, ___ ___?',
          answer: 'can\'t she',
          explanation: 'Positive with \'can\' → negative tag matching the modal: can\'t she.'
        },
        {
          text: 'You don\'t like spicy food, ___ ___?',
          answer: 'do you',
          explanation: 'Negative with \'don\'t\' → positive tag: do you.'
        },
        {
          text: 'There are many students here, ___ ___?',
          answer: 'aren\'t there',
          explanation: 'With \'there\' as subject, the tag uses \'there\': aren\'t there.'
        },
        {
          text: 'Nobody called, ___ ___?',
          answer: 'did they',
          explanation: '\'Nobody\' is negative, so the tag is positive. Use \'they\' for indefinite subjects.'
        }
      ]
    },
    {
      title: 'Identify the Error',
      type: 'select',
      instructions: 'Choose the sentence with the INCORRECT question tag.',
      questions: [
        {
          text: 'Which sentence has an error?',
          options: [
            'You like tea, isn\'t it?',
            'She works here, doesn\'t she?',
            'They have left, haven\'t they?'
          ],
          answer: 'You like tea, isn\'t it?',
          explanation: 'The correct tag is \'don\'t you\' (not \'isn\'t it\'). The tag must match the subject and auxiliary.'
        },
        {
          text: 'Which sentence has an error?',
          options: [
            'He should study, shouldn\'t he?',
            'I am late, amn\'t I?',
            'We can go, can\'t we?'
          ],
          answer: 'I am late, amn\'t I?',
          explanation: 'The correct tag for \'I am\' is \'aren\'t I\', not \'amn\'t I\' — that form doesn\'t exist.'
        },
        {
          text: 'Which sentence has an error?',
          options: [
            'She won\'t come, will she?',
            'They are students, don\'t they?',
            'He has finished, hasn\'t he?'
          ],
          answer: 'They are students, don\'t they?',
          explanation: 'The auxiliary is \'are\', so the tag should be \'aren\'t they\', not \'don\'t they\'.'
        },
        {
          text: 'Which sentence has an error?',
          options: [
            'Nothing happened, did it?',
            'She could help, couldn\'t she?',
            'You have a car, haven\'t you?'
          ],
          answer: 'You have a car, haven\'t you?',
          explanation: 'When \'have\' means possession (not auxiliary), the tag uses \'don\'t\': \'You have a car, don\'t you?\''
        }
      ]
    }
  ],
  tips: [
    'Positive → negative tag, negative → positive tag',
    'Match the auxiliary verb (is, have, can, will, etc.)',
    '\'I am\' → \'aren\'t I?\' (special case)',
    'Using tags in Speaking shows natural fluency'
  ],
  dailyPractice: [
    'Practice question tags concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext: 'Question Tags: Positive→negative, negative→positive. Match auxiliary verbs. Special cases: I am→aren\'t I, Let\'s→shall we. Shows natural fluency in IELTS Speaking.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...questionTagsData,
      title: questionTagsData.title + ' (Foundations)',
      overview: { ...questionTagsData.overview, duration: '15-20 minutes' },
      sections: questionTagsData.sections.slice(0, Math.max(2, Math.ceil(questionTagsData.sections.length / 2))),
      exercises: questionTagsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...questionTagsData,
      title: questionTagsData.title + ' (Advanced)',
      overview: { ...questionTagsData.overview, duration: '30-35 minutes' },
      sections: questionTagsData.sections,
      exercises: questionTagsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(questionTagsData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return questionTagsData;
}
